// @ts-nocheck
import React, { useCallback, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { SecondaryButtonInline, IconSpinner } from '../../../components';
import {
  authenticateWithWeb3Signature,
  connectInjectedEvmWallet,
  fetchWeb3AuthNonce,
  signWeb3AuthMessage,
} from '../../../util/web3Auth';
import { bootstrapUserPersona } from '../../../util/api';
import {
  PERSONA_TYPES,
  PENDING_INITIAL_PERSONA_KEY,
} from '../../../context/ActivePersonaContext';
import { personaCopy } from '../../../config/marketplaceContent';
import { useWalletSessionMaybe } from '../../../context/WalletSessionContext';

import css from './Web3WalletAuthPanel.module.css';

const PERSONA_OPTIONS = [
  { value: 'standard', title: personaCopy.signupStandardTitle, body: personaCopy.signupStandardBody },
  { value: 'merchant', title: personaCopy.signupMerchantTitle, body: personaCopy.signupMerchantBody },
  { value: 'runner', title: personaCopy.signupRunnerTitle, body: personaCopy.signupRunnerBody },
];

const resolveWorkspacePath = persona => {
  if (persona === 'merchant') {
    return '/merchant';
  }
  if (persona === 'runner') {
    return '/runner';
  }
  return '/';
};

const Web3WalletAuthPanel = props => {
  const { className, embedded = false, onAuthenticated, from } = props;
  const intl = useIntl();
  const walletSession = useWalletSessionMaybe();
  const [inProgress, setInProgress] = useState(false);
  const [error, setError] = useState(null);
  const [connectedAddress, setConnectedAddress] = useState(null);
  const [initialPersona, setInitialPersona] = useState('standard');

  const handleConnectWallet = useCallback(async () => {
    setError(null);
    setInProgress(true);

    try {
      if (walletSession?.authMode === 'web2') {
        throw new Error('Sign out of your account before connecting a wallet-only session.');
      }

      if (!PERSONA_TYPES.includes(initialPersona)) {
        throw new Error('Select a workspace persona before connecting your wallet.');
      }

      sessionStorage.setItem(PENDING_INITIAL_PERSONA_KEY, initialPersona);

      const { provider, walletAddress, chain } = await connectInjectedEvmWallet();
      setConnectedAddress(walletAddress);

      const { nonce, message } = await fetchWeb3AuthNonce({ walletAddress, chain });
      const signature = await signWeb3AuthMessage({ provider, message });
      const session = await authenticateWithWeb3Signature({
        walletAddress,
        signature,
        nonce,
        chain,
      });

      try {
        await bootstrapUserPersona(initialPersona);
      } catch {
        // Profile bootstrap may already exist — ActivePersonaContext will reconcile.
      }

      if (walletSession?.refreshSession) {
        await walletSession.refreshSession();
      }

      if (onAuthenticated) {
        onAuthenticated({
          session,
          from: from || resolveWorkspacePath(initialPersona),
          initialPersona,
        });
      }
    } catch (e) {
      sessionStorage.removeItem(PENDING_INITIAL_PERSONA_KEY);
      setError(
        e.code === 'NO_WALLET'
          ? intl.formatMessage({ id: 'Web3WalletAuthPanel.noWallet' })
          : e.message || intl.formatMessage({ id: 'Web3WalletAuthPanel.failed' })
      );
    } finally {
      setInProgress(false);
    }
  }, [from, initialPersona, intl, onAuthenticated, walletSession]);

  return (
    <div className={classNames(css.root, className, embedded ? css.embedded : null)}>
      {embedded ? null : (
        <div className={css.header}>
          <h3 className={css.title}>
            <FormattedMessage id="Web3WalletAuthPanel.title" />
          </h3>
          <p className={css.subtitle}>
            <FormattedMessage id="Web3WalletAuthPanel.subtitle" />
          </p>
        </div>
      )}

      <fieldset className={css.personaFieldset} data-testid="web3-persona-selection">
        <legend className={css.personaLegend}>{personaCopy.signupPersonaLegend}</legend>
        <div className={css.personaOptions}>
          {PERSONA_OPTIONS.map(option => (
            <label
              key={option.value}
              className={classNames(
                css.personaOption,
                initialPersona === option.value ? css.personaOptionActive : null
              )}
            >
              <input
                type="radio"
                name="web3InitialPersona"
                value={option.value}
                checked={initialPersona === option.value}
                onChange={() => setInitialPersona(option.value)}
              />
              <span className={css.personaOptionTitle}>{option.title}</span>
              <span className={css.personaOptionBody}>{option.body}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className={css.steps}>
        <div className={css.step}>
          <span className={css.stepNumber}>1</span>
          Connect MetaMask or Rabby
        </div>
        <div className={css.step}>
          <span className={css.stepNumber}>2</span>
          Sign the personal_sign message to link your account
        </div>
      </div>

      {connectedAddress ? (
        <p className={css.connectedAddress}>
          <FormattedMessage
            id="Web3WalletAuthPanel.connectedAddress"
            values={{ address: connectedAddress }}
          />
        </p>
      ) : null}

      {error ? <p className={css.error}>{error}</p> : null}

      <SecondaryButtonInline
        className={css.connectButton}
        onClick={handleConnectWallet}
        disabled={inProgress}
      >
        {inProgress ? (
          <span className={css.buttonContent}>
            <IconSpinner />
            <FormattedMessage id="Web3WalletAuthPanel.signing" />
          </span>
        ) : (
          <FormattedMessage id="Web3WalletAuthPanel.connectWallet" />
        )}
      </SecondaryButtonInline>

      <p className={css.hint}>
        <FormattedMessage id="Web3WalletAuthPanel.hint" />
      </p>
    </div>
  );
};

export default Web3WalletAuthPanel;
